import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckChip } from "@/components/CheckChip";
import { WatchFilter, watchOn, watchSummary } from "@/components/WatchFilter";

export const RATINGS = ["G", "PG", "PG-13", "R", "NC-17", "NR"];
const RATING_LABEL = { NR: "Not Rated" };
export const DEFAULT_WATCH = { mode: "any", providers: [], physical: false, money: [] };

let genreCache = null;
export function useGenres() {
  const [genres, setGenres] = useState(genreCache || []);
  useEffect(() => {
    if (genreCache) return;
    api.get("/genres").then(({ data }) => { genreCache = data; setGenres(data); });
  }, []);
  return genres;
}

export const normFilter = (f) => ({
  genres: f?.genres || [], ratings: f?.ratings || [], watch: { ...DEFAULT_WATCH, ...(f?.watch || {}) },
});
export const savedFilter = (user, profile) => normFilter(profile === "mood" ? user.mood : user);
export const filterKey = (f) => {
  const n = normFilter(f);
  const s = (a) => [...a].sort();
  return JSON.stringify([s(n.genres), s(n.ratings), n.watch.mode, s(n.watch.providers), n.watch.physical, s(n.watch.money)]);
};

export function filterSummary(filter, genres) {
  if (!filter) return "";
  const names = Object.fromEntries(genres.map((g) => [g.id, g.name]));
  const g = filter.genres?.length ? filter.genres.map((id) => names[id] || id).join(", ") : "All genres";
  const r = filter.ratings?.length ? filter.ratings.map((x) => RATING_LABEL[x] || x).join(", ") : "Any rating";
  const w = watchSummary(normFilter(filter).watch);
  return [g, r, w].filter(Boolean).join(" · ");
}

export const isEmptyFilter = (f) => !f?.genres?.length && !f?.ratings?.length && !watchOn(normFilter(f).watch);

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

function Section({ title, hint, children }) {
  return (
    <section>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="font-display font-bold text-lg">{title}</h3>
        <span className="text-xs text-muted-foreground text-right">{hint}</span>
      </div>
      {children}
    </section>
  );
}

export function FilterEditor({ value, onChange, prefix }) {
  const genres = useGenres();
  const v = normFilter(value);
  return (
    <div className="space-y-10">
      <Section title="Genres" hint={v.genres.length ? `${v.genres.length} selected · matches any` : "None selected = all genres"}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {genres.map((g) => (
            <CheckChip key={g.id} on={v.genres.includes(g.id)} testId={`${prefix}-genre-${g.id}`}
              onClick={() => onChange({ ...v, genres: toggle(v.genres, g.id) })}>{g.name}</CheckChip>
          ))}
        </div>
      </Section>
      <Section title="MPAA rating" hint={v.ratings.length ? `${v.ratings.length} selected` : "None selected = any rating"}>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {RATINGS.map((r) => (
            <CheckChip key={r} on={v.ratings.includes(r)} testId={`${prefix}-rating-${r}`}
              onClick={() => onChange({ ...v, ratings: toggle(v.ratings, r) })}>{RATING_LABEL[r] || r}</CheckChip>
          ))}
        </div>
      </Section>
      <WatchFilter prefix={prefix} value={v.watch} onChange={(watch) => onChange({ ...v, watch })} />
    </div>
  );
}
